/**
 * Build MUI Data Grid column filter operators from GET /regulations/fields config.
 */

import type { GridFilterOperator } from "@mui/x-data-grid-premium";
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
} from "@mui/x-data-grid-premium";
import type { FieldConfig } from "../../../services/regulationsService";
import { GridFilterDatePickerInput } from "./GridFilterDatePickerInput";

/** Use MUI DatePicker (DD-MMM-YYYY) instead of native date input (locale-dependent 年/月/日). */
function mapDateOperatorsToPickerInput(
  operators: GridFilterOperator[],
): GridFilterOperator[] {
  return operators.map((op) =>
    op.InputComponent != null
      ? {
          ...op,
          InputComponent: GridFilterDatePickerInput,
          InputComponentProps: op.InputComponentProps,
        }
      : op,
  );
}

/**
 * Map API / backend filter operator names to canonical tokens.
 * Date: eq, ne, gt, lt, gte, lte; optional isempty / isnotempty (not bundled with eq/ne).
 * String/array: contains, eq, ne, startswith, endswith, like, in, gt/gte/lt/lte;
 *   isempty / isnotempty only when listed (not bundled with eq/ne).
 */
function mapOperatorTokenToCanonical(raw: string): string {
  const compact = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  if (!compact) return "";

  const aliases: Record<string, string> = {
    "=": "eq",
    "==": "eq",
    equals: "eq",
    equal: "eq",
    eq: "eq",
    is: "eq",
    "!=": "ne",
    ne: "ne",
    neq: "ne",
    not: "ne",
    notequal: "ne",
    notequals: "ne",
    doesnotequal: "ne",
    does_notequal: "ne",
    does_not_equal: "ne",
    not_equal: "ne",
    "<>": "ne",
    ">": "gt",
    gt: "gt",
    greaterthan: "gt",
    greater_than: "gt",
    after: "gt",
    ">=": "gte",
    gte: "gte",
    ge: "gte",
    greaterorequal: "gte",
    greater_than_or_equal: "gte",
    onorafter: "gte",
    "<": "lt",
    lt: "lt",
    lessthan: "lt",
    less_than: "lt",
    before: "lt",
    "<=": "lte",
    lte: "lte",
    le: "lte",
    lessorequal: "lte",
    less_than_or_equal: "lte",
    onorbefore: "lte",
    contains: "contains",
    // API uses snake_case; keep distinct from generic "like"
    starts_with: "startswith",
    startswith: "startswith",
    ends_with: "endswith",
    endswith: "endswith",
    like: "like",
    in: "in",
    isanyof: "in",
    isempty: "isempty",
    is_empty: "isempty",
    empty: "isempty",
    isnotempty: "isnotempty",
    is_not_empty: "isnotempty",
    isnot_null: "isnotempty",
    notempty: "isnotempty",
    isnull: "isempty",
    is_null: "isempty",
  };

  if (aliases[compact]) return aliases[compact];
  if (compact === "neq") return "ne";
  return compact;
}

function operatorsByValue(
  list: GridFilterOperator[],
): Record<string, GridFilterOperator> {
  return Object.fromEntries(list.map((o) => [o.value, o]));
}

/**
 * Display labels for API `filter_operators` tokens.
 * MUI resolves `filterOperator{Value}` locale keys when `label` is missing — custom API
 * tokens like `eq` / `starts_with` are not in the default bundle.
 */
const OPERATOR_DISPLAY_LABEL: Record<string, string> = {
  contains: "contains",
  eq: "equals",
  ne: "does not equal",
  isempty: "is empty",
  isnotempty: "is not empty",
  startswith: "starts with",
  endswith: "ends with",
  like: "contains",
  in: "is any of",
  gt: "greater than",
  gte: "greater than or equal to",
  lt: "less than",
  lte: "less than or equal to",
};

/** Date columns: natural-language labels (not comparison symbols). */
const DATE_OPERATOR_DISPLAY_LABEL: Record<string, string> = {
  gt: "after",
  gte: "on or after",
  lt: "before",
  lte: "on or before",
};

function resolveOperatorDisplayLabel(
  base: GridFilterOperator | undefined,
  canon: string,
  apiOperator: string,
  labelOverride?: string,
): string {
  if (labelOverride != null && labelOverride !== "") return labelOverride;
  if (typeof base?.label === "string" && base.label !== "") return base.label;
  return OPERATOR_DISPLAY_LABEL[canon] ?? apiOperator;
}

/** Server-side filter: avoid numeric parse on string cells; API applies the real predicate. */
function numericOperatorAsTextForStringColumn(
  base: GridFilterOperator | undefined,
): GridFilterOperator | undefined {
  if (!base) return undefined;
  return {
    ...base,
    InputComponentProps: {
      ...base.InputComponentProps,
      type: "text",
    },
    getApplyFilterFn: () => () => true,
  };
}

function apiFilterOperatorTokens(field: FieldConfig): string[] {
  return (field.filter_operators ?? [])
    .map((s) => String(s).trim())
    .filter(Boolean);
}

/** Column is filterable only when API marks it filterable and returns `filter_operators`. */
export function isFieldConfigColumnFilterable(field: FieldConfig): boolean {
  return field.filterable && apiFilterOperatorTokens(field).length > 0;
}

/**
 * Operators shown in the column filter panel for one field.
 * Requires non-empty API `filter_operators`; no frontend fallback set.
 */
export function gridFilterOperatorsFromFieldConfig(
  field: FieldConfig,
): GridFilterOperator[] | undefined {
  if (!isFieldConfigColumnFilterable(field)) {
    return undefined;
  }

  const rawOps = apiFilterOperatorTokens(field);

  const str = operatorsByValue(getGridStringOperators());
  const date = operatorsByValue(getGridDateOperators(false));
  const num = operatorsByValue(getGridNumericOperators());
  const ordered: GridFilterOperator[] = [];
  const seen = new Set<string>();

  /** Use backend `filter_operators` token as DataGrid `operator` value so search echoes API verbatim. */
  const pushApiOperator = (
    base: GridFilterOperator | undefined,
    apiOperator: string,
    canon: string,
    labelOverride?: string,
  ) => {
    if (!base || seen.has(apiOperator)) return;
    seen.add(apiOperator);
    ordered.push({
      ...base,
      value: apiOperator,
      label: resolveOperatorDisplayLabel(base, canon, apiOperator, labelOverride),
      getApplyFilterFn: base.getApplyFilterFn ?? (() => () => true),
    });
  };

  for (const raw of rawOps) {
    const canon = mapOperatorTokenToCanonical(raw);
    if (!canon) continue;

    if (field.field_type === "date") {
      switch (canon) {
        case "eq":
          pushApiOperator(date.is, raw, canon);
          break;
        case "isempty":
          pushApiOperator(date.isEmpty, raw, canon);
          break;
        case "ne":
          pushApiOperator(date.not, raw, canon);
          break;
        case "isnotempty":
          pushApiOperator(date.isNotEmpty, raw, canon);
          break;
        case "gt":
          pushApiOperator(
            date.after,
            raw,
            canon,
            DATE_OPERATOR_DISPLAY_LABEL.gt,
          );
          break;
        case "gte":
          pushApiOperator(
            date.onOrAfter,
            raw,
            canon,
            DATE_OPERATOR_DISPLAY_LABEL.gte,
          );
          break;
        case "lt":
          pushApiOperator(
            date.before,
            raw,
            canon,
            DATE_OPERATOR_DISPLAY_LABEL.lt,
          );
          break;
        case "lte":
          pushApiOperator(
            date.onOrBefore,
            raw,
            canon,
            DATE_OPERATOR_DISPLAY_LABEL.lte,
          );
          break;
        default:
          break;
      }
      continue;
    }

    // String and array: follow API order; empty / not-empty only when filter_operators lists them.
    switch (canon) {
      case "contains":
        pushApiOperator(str.contains, raw, canon);
        break;
      case "eq":
        pushApiOperator(str.equals, raw, canon);
        break;
      case "ne":
        pushApiOperator(str.doesNotEqual, raw, canon);
        break;
      case "isempty":
        pushApiOperator(str.isEmpty, raw, canon);
        break;
      case "isnotempty":
        pushApiOperator(str.isNotEmpty, raw, canon);
        break;
      case "startswith":
        pushApiOperator(str.startsWith, raw, canon);
        break;
      case "endswith":
        pushApiOperator(str.endsWith, raw, canon);
        break;
      case "like":
        pushApiOperator(str.contains, raw, canon, "contains (LIKE)");
        break;
      case "in":
        pushApiOperator(str.isAnyOf, raw, canon);
        break;
      case "gt":
        pushApiOperator(
          numericOperatorAsTextForStringColumn(num[">"]),
          raw,
          canon,
          OPERATOR_DISPLAY_LABEL.gt,
        );
        break;
      case "gte":
        pushApiOperator(
          numericOperatorAsTextForStringColumn(num[">="]),
          raw,
          canon,
          OPERATOR_DISPLAY_LABEL.gte,
        );
        break;
      case "lt":
        pushApiOperator(
          numericOperatorAsTextForStringColumn(num["<"]),
          raw,
          canon,
          OPERATOR_DISPLAY_LABEL.lt,
        );
        break;
      case "lte":
        pushApiOperator(
          numericOperatorAsTextForStringColumn(num["<="]),
          raw,
          canon,
          OPERATOR_DISPLAY_LABEL.lte,
        );
        break;
      default:
        break;
    }
  }

  if (ordered.length === 0) {
    return undefined;
  }

  return field.field_type === "date"
    ? mapDateOperatorsToPickerInput(ordered)
    : ordered;
}

/**
 * Fallback locale keys when MUI looks up `filterOperator{CapitalizedValue}` for API tokens.
 * @see https://mui.com/x/react-data-grid/filtering/#filter-operators
 */
export const REGULATIONS_GRID_FILTER_LOCALE_TEXT = {
  filterOperatorEq: "equals",
  filterOperatorNe: "does not equal",
  filterOperatorContains: "contains",
  filterOperatorStarts_with: "starts with",
  filterOperatorEnds_with: "ends with",
  filterOperatorLike: "contains (LIKE)",
  filterOperatorIn: "is any of",
  filterOperatorGt: "greater than",
  filterOperatorGte: "greater than or equal to",
  filterOperatorLt: "less than",
  filterOperatorLte: "less than or equal to",
  filterOperatorIsempty: "is empty",
  filterOperatorIs_empty: "is empty",
  filterOperatorIsnotempty: "is not empty",
  filterOperatorIs_not_empty: "is not empty",
} as const;
